import { interactionShader } from './interaction-shader'

// Shared uniform layouts are mirrored by resources.ts and camera.ts.
const uniforms = /* wgsl */ `
struct Frame {
  viewProjection: mat4x4<f32>,
  lightViewProjection: mat4x4<f32>,
  eye: vec4<f32>, // camera position, CSS pixel ratio
  light: vec4<f32>,
};
struct Object {
  model: mat4x4<f32>,
  normal: mat4x4<f32>,
  color: vec4<f32>,
  material: vec4<f32>, // roughness, metalness, textured, highlighted
  surface: vec4<f32>, // felt, foreground, back artwork, signed interaction state
};
@group(0) @binding(0) var<uniform> frame: Frame;
@group(1) @binding(0) var<uniform> object: Object;
struct VertexIn {
  @location(0) position: vec3<f32>,
  @location(1) normal: vec3<f32>,
  @location(2) uv: vec2<f32>,
};
`

export const shadowShader = uniforms + /* wgsl */ `
@vertex fn shadowVertex(vertex: VertexIn) -> @builtin(position) vec4<f32> {
  return frame.lightViewProjection * object.model * vec4<f32>(vertex.position, 1.0);
}
`

export const tableShader = uniforms + /* wgsl */ `
@group(0) @binding(1) var shadowMap: texture_depth_2d;
@group(0) @binding(2) var shadowSampler: sampler_comparison;
@group(1) @binding(1) var faceTexture: texture_2d<f32>;
@group(1) @binding(2) var faceSampler: sampler;
@group(1) @binding(3) var backTexture: texture_2d<f32>;

struct VertexOut {
  @builtin(position) position: vec4<f32>,
  @location(0) world: vec3<f32>,
  @location(1) normal: vec3<f32>,
  @location(2) uv: vec2<f32>,
  @location(3) localNormal: vec3<f32>,
  @location(4) shadow: vec4<f32>,
  @location(5) localPosition: vec3<f32>,
};
@vertex fn tableVertex(vertex: VertexIn) -> VertexOut {
  var output: VertexOut;
  let world = object.model * vec4<f32>(vertex.position, 1.0);
  output.position = frame.viewProjection * world;
  output.world = world.xyz;
  output.normal = normalize((object.normal * vec4<f32>(vertex.normal, 0.0)).xyz);
  output.localNormal = vertex.normal;
  output.localPosition = vertex.position;
  output.uv = vertex.uv;
  output.shadow = frame.lightViewProjection * world;
  return output;
}

fn shadowVisibility(position: vec4<f32>, normal: vec3<f32>, light: vec3<f32>) -> f32 {
  let projected = position.xyz / position.w;
  let uv = projected.xy * vec2<f32>(0.5, -0.5) + vec2<f32>(0.5);
  if (any(uv < vec2<f32>(0.0)) || any(uv > vec2<f32>(1.0)) || projected.z > 1.0 || projected.z < 0.0) {
    return 1.0;
  }
  let bias = max(0.00012, 0.00065 * (1.0 - max(dot(normal, light), 0.0)));
  let texel = 1.0 / vec2<f32>(textureDimensions(shadowMap));
  var result = 0.0;
  for (var y = -1; y <= 1; y += 1) {
    for (var x = -1; x <= 1; x += 1) {
      result += textureSampleCompareLevel(shadowMap, shadowSampler, uv + vec2<f32>(f32(x), f32(y)) * texel, projected.z - bias);
    }
  }
  return result / 9.0;
}

fn hash(p: vec2<f32>) -> f32 {
  return fract(sin(dot(p, vec2<f32>(127.1, 311.7))) * 43758.5453);
}
fn srgbToLinear(color: vec3<f32>) -> vec3<f32> {
  return select(pow((color + vec3<f32>(0.055)) / 1.055, vec3<f32>(2.4)), color / 12.92, color <= vec3<f32>(0.04045));
}
fn linearToSrgb(color: vec3<f32>) -> vec3<f32> {
  let positive = max(color, vec3<f32>(0.0));
  return select(1.055 * pow(positive, vec3<f32>(1.0 / 2.4)) - vec3<f32>(0.055), positive * 12.92, positive <= vec3<f32>(0.0031308));
}
fn filmic(color: vec3<f32>) -> vec3<f32> {
  let a = color * (2.51 * color + vec3<f32>(0.03));
  let b = color * (2.43 * color + vec3<f32>(0.59)) + vec3<f32>(0.14);
  return clamp(a / b, vec3<f32>(0.0), vec3<f32>(1.0));
}

` + interactionShader + /* wgsl */ `
@fragment fn tableFragment(input: VertexOut) -> @location(0) vec4<f32> {
  let textureColor = textureSample(faceTexture, faceSampler, input.uv).rgb;
  let backColor = textureSample(backTexture, faceSampler, input.uv).rgb;
  let topWeight = object.material.z * smoothstep(0.85, 0.99, input.localNormal.y);
  let backWeight = object.surface.z * smoothstep(0.85, 0.99, -input.localNormal.y);
  let printWeight = max(topWeight, backWeight);
  var base = mix(srgbToLinear(object.color.rgb), textureColor, topWeight);
  base = mix(base, backColor, backWeight);
  let normal = normalize(input.normal);
  let light = normalize(frame.light.xyz);
  let view = normalize(frame.eye.xyz - input.world);
  let halfway = normalize(light + view);
  let roughness = clamp(object.material.x, 0.08, 1.0);
  let metalness = object.material.y;
  let diffuse = max(dot(normal, light), 0.0);
  let visibility = mix(shadowVisibility(input.shadow, normal, light), 1.0, object.surface.y);
  // Fade microfibers once they become subpixel, so orbiting cannot make the
  // tabletop sparkle. The larger soft grain remains visible at play distance.
  let footprint = max(length(dpdx(input.world.xz)), length(dpdy(input.world.xz)));
  let fiberVisibility = 1.0 - smoothstep(0.001, 0.005, footprint);
  let grain = hash(floor(input.world.xz * 680.0));
  let weave = sin(input.world.x * 880.0) * sin(input.world.z * 860.0);
  let softGrain = sin(input.world.x * 6.7 + sin(input.world.z * 5.3)) * sin(input.world.z * 8.9);
  base *= 1.0 + object.surface.x * (softGrain * 0.014 + fiberVisibility * ((grain - 0.5) * 0.055 + weave * 0.012));
  let sky = mix(vec3<f32>(0.19, 0.21, 0.23), vec3<f32>(0.46, 0.48, 0.50), normal.y * 0.5 + 0.5);
  let ambient = base * sky;
  let sunlight = vec3<f32>(1.0, 0.98, 0.95) * frame.light.w;
  let exponent = mix(110.0, 5.0, roughness * roughness);
  let fresnel = mix(vec3<f32>(0.04), base, metalness);
  let specular = pow(max(dot(normal, halfway), 0.0), exponent) * pow(1.0 - roughness, 2.0);
  let lit = ambient + (base * diffuse * (1.0 - metalness * 0.65) + fresnel * specular * 2.0) * sunlight * visibility;

  // Printed paper is a matte, color-managed surface. Scene exposure and glossy
  // highlights must not turn dark ink gray or tint the paper. Retain directional
  // form and contact shadows, while preserving the authored text contrast.
  let printLight = (0.86 + 0.14 * diffuse) * mix(0.76, 1.0, visibility);
  var mapped = mix(filmic(lit), base * printLight, printWeight);
  mapped = interactionSurface(input, mapped);
  // Regions preserve the printed board underneath. Only their luminous rim is inked.
  if (object.color.a == 0.0) {
    let edge = abs(interactionEdgePixels(0.46 - max(abs(input.localPosition.x), abs(input.localPosition.z))));
    if (object.surface.w == 0.0 || input.localNormal.y < 0.9 || edge > 2.0 + object.material.w) { discard; }
    // A high-contrast rim remains readable over pale printed maps. The center
    // stays transparent, and a selected multi-space target uses the payment amber.
    mapped = select(vec3<f32>(0.08, 0.65, 1.0), vec3<f32>(1.0, 0.48, 0.04), object.surface.w < 0.0);
  }
  if (object.color.a > 0.0) { mapped *= object.color.a; }
  let color = linearToSrgb(mapped);
  return vec4<f32>(color, 1.0);
}
`
